import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, BedDouble, Bath, Maximize, MapPin, Tag, Calendar, Phone, Mail, Check, Lock, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const tx = (lang, fr, en, pt) => lang === 'fr' ? fr : lang === 'pt' ? pt : en;

const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price);
};

const typeLabels = {
  fr: { villa: 'Villa', apartment: 'Appartement', penthouse: 'Penthouse', land: 'Terrain', commercial: 'Commercial' },
  en: { villa: 'Villa', apartment: 'Apartment', penthouse: 'Penthouse', land: 'Land', commercial: 'Commercial' },
};

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const res = await axios.get(`${API}/api/properties/${id}`, { withCredentials: true });
        if (res.data.requires_auth) {
          navigate('/login', { state: { from: `/property/${id}` } });
          return;
        }
        setProperty(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? '404' : 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Header />
        <div className="flex flex-col items-center gap-4 pt-24" data-testid="property-loading">
          <div className="w-10 h-10 border-2 border-[#E5B1A1] border-t-transparent rounded-full animate-spin" />
          <span className="text-[#6B6B6B] text-sm tracking-wide">
            {tx(language, 'Chargement du dossier...', 'Loading file...', 'A carregar o dossier...')}
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex flex-col items-center justify-center pt-40 px-6" data-testid="property-error">
          <div className="w-20 h-20 border-2 border-[#E5B1A1]/30 flex items-center justify-center mb-6">
            <Lock size={28} className="text-[#E5B1A1]" />
          </div>
          <h2 className="font-serif text-2xl text-[#1A1A1B] mb-3">
            {error === '404'
              ? (tx(language, 'Dossier introuvable', 'File not found', 'Dossier não encontrado'))
              : (tx(language, 'Erreur de chargement', 'Loading error', 'Erro de carregamento'))}
          </h2>
          <p className="text-[#6B6B6B] mb-8">
            {tx(language, "Ce bien n'est plus disponible ou l'URL est incorrecte.", 'This property is no longer available or the URL is incorrect.', 'Este imóvel já não está disponível ou o URL está incorreto.')}
          </p>
          <Link
            to="/#portfolio"
            className="flex items-center gap-2 text-[#E5B1A1] hover:text-[#D49B8B] transition-colors font-medium"
            data-testid="back-to-portfolio-error"
          >
            <ArrowLeft size={18} />
            {tx(language, 'Retour au Portfolio', 'Back to Portfolio', 'Voltar ao Portfólio')}
          </Link>
        </div>
      </div>
    );
  }

  const title = language === 'pt' ? (property.title_pt || property.title_en) : language === 'fr' ? property.title_fr : property.title_en;
  const description = language === 'pt' ? (property.description_pt || property.description_en) : language === 'fr' ? property.description_fr : property.description_en;
  const features = language === 'pt' ? (property.features_pt || property.features_en) : language === 'fr' ? property.features_fr : property.features_en;
  const images = property.images || [];
  const pType = typeLabels[language]?.[property.property_type] || property.property_type;

  const stats = [
    { icon: BedDouble, value: property.bedrooms, label: tx(language, 'Chambres', 'Bedrooms', 'Quartos') },
    { icon: Bath, value: property.bathrooms, label: tx(language, 'Salles de bain', 'Bathrooms', 'Casas de banho') },
    { icon: Maximize, value: `${property.surface} m²`, label: 'Surface' },
    { icon: Tag, value: pType, label: tx(language, 'Type', 'Type', 'Type') },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="property-detail-page">
      <Header />

      {/* BREADCRUMB + BACK */}
      <div className="bg-white pt-28 pb-4 border-b border-[#E5B1A1]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Link
            to="/#portfolio"
            className="inline-flex items-center gap-2 text-sm text-[#6B6B6B] hover:text-[#E5B1A1] transition-colors"
            data-testid="back-to-portfolio"
          >
            <ArrowLeft size={16} />
            {tx(language, 'Retour au Portfolio', 'Back to Portfolio', 'Voltar ao Portfólio')}
          </Link>
        </div>
      </div>

      {/* HERO IMAGE */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
          <div className="grid lg:grid-cols-3 gap-4">
            {/* Main image */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2 aspect-[16/10] overflow-hidden bg-[#F5F5F5]"
            >
              <img
                src={images[activeImg] || images[0]}
                alt={title}
                className="w-full h-full object-cover"
                data-testid="property-main-image"
              />
            </motion.div>

            {/* Side thumbnails or info card */}
            <div className="flex flex-row lg:flex-col gap-4">
              {images.length > 1 ? (
                images.slice(0, 3).map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`flex-1 aspect-[16/10] lg:aspect-auto overflow-hidden border-2 transition-all ${
                      activeImg === i ? 'border-[#E5B1A1]' : 'border-transparent hover:border-[#E5B1A1]/40'
                    }`}
                    data-testid={`thumbnail-${i}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))
              ) : (
                <div className="flex-1 bg-[#1A1A1B] p-8 flex flex-col justify-center">
                  <span className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] font-medium mb-3">
                    {tx(language, 'INVESTISSEMENT', 'INVESTMENT', 'INVESTIMENTO')}
                  </span>
                  <span className="font-serif text-3xl text-white mb-2">
                    {formatPrice(property.price)}
                  </span>
                  <span className="text-[#A3A3A0] text-sm flex items-center gap-2 mt-2">
                    <MapPin size={14} className="text-[#E5B1A1]" />
                    {property.location}
                  </span>
                  {property.is_off_market && (
                    <span className="mt-4 inline-flex items-center gap-2 text-xs text-[#E5B1A1] uppercase tracking-wider">
                      <Lock size={12} />
                      Off-Market
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* TITLE + PRICE BAR */}
      <section className="bg-[#1A1A1B]" data-testid="property-title-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h1 className="font-serif text-3xl lg:text-4xl text-white mb-3" data-testid="property-title">
                {title}
              </h1>
              <div className="flex items-center gap-3 text-[#A3A3A0]">
                <MapPin size={16} className="text-[#E5B1A1]" />
                <span data-testid="property-location">{property.location}</span>
                {property.is_off_market && (
                  <>
                    <span className="text-[#E5B1A1]/30">|</span>
                    <span className="text-xs text-[#E5B1A1] uppercase tracking-wider flex items-center gap-1">
                      <Lock size={11} /> Off-Market
                    </span>
                  </>
                )}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-right"
            >
              <span className="block text-[#A3A3A0] text-xs uppercase tracking-wider mb-1">
                {tx(language, 'Prix', 'Price', 'Preço')}
              </span>
              <span className="font-serif text-3xl lg:text-4xl text-[#E5B1A1]" data-testid="property-price">
                {formatPrice(property.price)}
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* KEY STATS */}
      <section className="bg-white border-b border-[#E5B1A1]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="property-stats">
            {stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 * i }}
                className="flex items-center gap-4 p-4 border border-[#E5B1A1]/15"
              >
                <div className="w-11 h-11 bg-[#1A1A1B] flex items-center justify-center flex-shrink-0">
                  <s.icon size={20} strokeWidth={1.5} className="text-[#E5B1A1]" />
                </div>
                <div>
                  <span className="block text-lg font-semibold text-[#1A1A1B]">{s.value}</span>
                  <span className="block text-xs text-[#6B6B6B] uppercase tracking-wider">{s.label}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DESCRIPTION + FEATURES */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="grid lg:grid-cols-5 gap-16">
            {/* Description — 3 cols */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="lg:col-span-3"
            >
              <h2 className="text-xs text-[#E5B1A1] uppercase tracking-[0.2em] font-medium mb-6">
                {tx(language, 'DESCRIPTION DU BIEN', 'PROPERTY DESCRIPTION', 'DESCRIÇÃO DO IMÓVEL')}
              </h2>
              <p className="text-[#1A1A1B] leading-relaxed text-base lg:text-lg" data-testid="property-description">
                {description}
              </p>
            </motion.div>

            {/* Features — 2 cols */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="lg:col-span-2"
            >
              <h2 className="text-xs text-[#E5B1A1] uppercase tracking-[0.2em] font-medium mb-6">
                {tx(language, 'CARACTÉRISTIQUES', 'FEATURES', 'CARACTERÍSTICAS')}
              </h2>
              <ul className="space-y-4" data-testid="property-features">
                {features?.map((feat, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#1A1A1B]">
                    <div className="w-6 h-6 border border-[#E5B1A1]/40 flex items-center justify-center flex-shrink-0">
                      <Check size={14} className="text-[#E5B1A1]" />
                    </div>
                    {feat}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-[#1A1A1B]" data-testid="property-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="font-serif text-2xl text-white mb-2">
                {tx(language, 'Intéressé par ce bien ?', 'Interested in this property?', 'Interessado neste imóvel?')}
              </h3>
              <p className="text-[#A3A3A0] text-sm">
                {tx(language, 'Contactez-nous pour une consultation personnalisée et confidentielle.', 'Contact us for a personalized and confidential consultation.', 'Contacte-nos para uma consulta personalizada e confidencial.')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => navigate('/#contact')}
                className="px-8 py-3 text-sm font-medium border-0 text-white"
                style={{ backgroundColor: '#E5B1A1' }}
                data-testid="property-cta-contact"
              >
                <Mail size={16} className="mr-2" />
                {tx(language, 'Nous contacter', 'Contact us', 'Contactar-nos')}
              </Button>
              <a
                href="tel:+33659931384"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 border border-[#E5B1A1]/30 text-[#E5B1A1] hover:bg-[#E5B1A1]/10 transition-colors text-sm font-medium"
                data-testid="property-cta-call"
              >
                <Phone size={16} />
                +33 6 59 93 13 84
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
